import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useStreamData } from '@lib/hooks/useStreamData';
import {
  getMapProcessedCommentsCount
} from '@services/youtube-platform.service';
import { pushDataLayerForEvent } from '@lib/gtag';
import db from '@services/firebase-service';

import { doc, onSnapshot } from 'firebase/firestore';
import debounce from 'lodash.debounce';
import MapBoxComponent from './MapBoxComponent';
import DownArrow from '../../public/images/InteractionPage/dynamic-svg/down-arrow.svg';
import { logToCloudWatch } from '@services/logger.service';
import { handleEngagement, mapContinents } from '@services/interactions-service';

const Map = forwardRef(({
  streamId, platformType, setShowRosModel, interactionState,
  currentRosResult, currentRosFeature, interactionType, startInteraction, setCurrentFeatureId,
  moderationModeRef, activeInteractionRef, updateFSMeta,
  setShowNotification, setStartTime,
  setTotalResponders,
  isMuted
}, ref) => {
  const { isResult, isRos } = interactionType;

  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setMapBox,
    setOverrideDismissStatus,
    setFeatureId,
    setFeatureSettings,
    selectedLocation,
    setSelectedLocation
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setMapBox: s.setMapBox,
    setOverrideDismissStatus: s.setOverrideDismissStatus,
    setFeatureId: s.setFeatureId,
    setFeatureSettings: s.setFeatureSettings,
    selectedLocation: s.selectedLocation,
    setSelectedLocation: s.setSelectedLocation
  }));

  const [mapId, setMapId] = useState(null);
  const [mapResult, setMapResult] = useState([]);

  const [isDropdownVisible, setDropdownVisible] = useState(false);

  const updateMapboxView = (continent) => {
    if (continent.bounds && process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      setMapBox({ type: 'fitBounds', bounds: continent.bounds, padding: 50 });
    } else {
      setMapBox({
        type: 'viewRegion',
        viewRegion: {
          center: continent.coordinates,
          zoom: continent.zoom
        }
      });
    }
  };
  const updateLocation = () => {
    const continent = mapContinents.find((continent) => continent.value === currentRosFeature.setting.DefaultView);
    if (continent) {
      setSelectedLocation(continent.name);
      updateMapboxView(continent);
    }
  };

  const setupMap = ({ id, settings, noOfResponders, interactionResult }) => {
    setMapId(id);
    setCurrentFeatureId(id);
    setFeatureId(id);
    setFeatureSettings(settings);
    setMapBox({ type: 'clean' });
    updateLocation();
    setTotalResponders(noOfResponders);
    setMapResult(interactionResult);
  };

  const handleMap = async ({ interactionId = null, interactionResult }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      };
      setupMap(interactionDetails);
      updateFSMeta({ activeInteraction: { ...interactionDetails, type: 'map' } });
    }
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  const stopMap = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'map', { engagementId: mapId, platformType, sessionId });
  };

  useEffect(() => {
    if (mapId && isRos) {
      const logData = {
        streamId,
        interactionType: 'maps',
        interactionId: mapId
      };
      setFirestoreUnsub({
        unsub: onSnapshot(doc(db, 'streams', streamId, 'maps', mapId), debounce((document) => {
          if (document.exists()) {
            const features = document.data().data.map((d) => ({
              type: 'Feature',
              geometry: d.geo_json.geometry,
              properties: d.geo_json.properties
            }));

            if (features.length > 0) setShowNotification(false);

            setMapBox({ type: 'update', features });

            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, 1500, { maxWait: 1500 }), (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [mapId]);

  useEffect(() => {
    if (activeInteractionRef.current && activeInteractionRef.current?.type === 'map') {
      setupMap({
        id: activeInteractionRef.current.id,
        colors: activeInteractionRef.current.colors,
        settings: activeInteractionRef.current.settings,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0,
        interactionResult: activeInteractionRef.current.interactionResult || []
      });
    }
  }, [activeInteractionRef.current]);

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleMap({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleMap({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopMap();
    SendDataToGoogleAnalytics();
    setMapId(null);
  };

  const manageMapInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (mapId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    if (moderationModeRef.current) return;

    manageMapInteraction();

    setOverrideDismissStatus(new Date().getTime());
  }, [currentRosFeature.id, startInteraction]);

  useEffect(() => {
    setMapBox({ type: 'clean' });
  }, [currentRosFeature.id, currentRosResult]);

  useEffect(() => {
    if (currentRosFeature?.setting?.DefaultView) {
      updateLocation();
    }
  }, [currentRosFeature?.setting?.DefaultView]);

  const SendDataToGoogleAnalytics = async () => {
    const response = await getMapProcessedCommentsCount(mapId);
    if (response.status && response.entity) {
      pushDataLayerForEvent('end_magic_map_interaction', { no_count: response.entity });
    }
  };

  useImperativeHandle(ref, () => ({
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleMap({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  return (<>
    <div className={'w-full h-full'}>
      <div className={'relative h-full rounded-lg overflow-hidden'}>
        <div className='relative z-10 cursor-pointer'>
          <div
            onMouseEnter={() => setDropdownVisible(true)}
            onMouseLeave={() => setDropdownVisible(false)}
            className="flex items-center absolute left-6 top-2.5 bg-neutral-80 rounded py-2 px-3 text-neutral-50 h-8 w-38 text-sm leading-3.5 "
          >
            <p className='w-full '>{selectedLocation}</p>
            <DownArrow width='12' height="6" className="fill-current text-neutral-50 cursor-pointer ml-0" />
          </div>
          <div
            onMouseEnter={() => setDropdownVisible(true)}
            onMouseLeave={() => setDropdownVisible(false)}
            className={`${isDropdownVisible ? 'flex' : 'hidden'} flex-col gap-y-2 absolute left-6 top-10 bg-neutral-80 rounded px-3 py-2 text-neutral-50 w-38 text-sm leading-3.5`}
          >
            {mapContinents.map((continent, index) =>
              <p key={index}
                onClick={() => {
                  setSelectedLocation(continent.name);
                  updateMapboxView(continent);
                }}
                className='hover:text-white cursor-pointer text-neutral-50 font-medium font-Poppins text-sm leading-3.5 whitespace-nowrap'>{continent.name}</p>)}
          </div>
        </div>
        <MapBoxComponent
          streamId={streamId}
          mapId={mapId}
          interactionState={interactionState}
          colors={currentRosFeature.color}
          settings={currentRosFeature.setting}
          currentRosResult={mapResult}
          isMuted={isMuted}
        />
      </div >
    </div >
  </>);
});

export default Map;
